import React, { type ReactNode } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import Link from "next/link";
import { Chip } from "ui/chips";
import { DirectionalButton } from "ui/button";
import { type BrandColorVariants, getBrandColorHex, getTransparentBrandColorHex } from "theme/colors";

export const PlatformVariants = {
  AGENTS: "agents",
  AID: "aid",
  CDP: "cdp",
};

export type PlatformCardVariant = (typeof PlatformVariants)[keyof typeof PlatformVariants];

type PlatformCardProps = {
  isHovered?: boolean;
  isHidden?: boolean;
  eyebrow: string;
  title: string;
  description: string;
  href: string;
  ctaText: string;
  media: ReactNode;
  variant: PlatformCardVariant;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export function PlatformCard({
  isHovered,
  isHidden,
  eyebrow,
  title,
  description,
  href,
  variant,
  ctaText,
  media,
  // Color,
  onMouseEnter,
  onMouseLeave,
}: PlatformCardProps) {
  let color: keyof BrandColorVariants = "blue";
  const bgImage = `url(/assets/home/${variant}-card-bg.png)`;

  switch (variant) {
    case PlatformVariants.AGENTS: {
      color = "blue";
      break;
    }

    case PlatformVariants.AID: {
      color = "violet";
      break;
    }

    case PlatformVariants.CDP: {
      color = "green";
      break;
    }

    // No default
  }

  const baseColor = getTransparentBrandColorHex(color, 50, 0.5);
  const endColor = getBrandColorHex(color, 150);
  return (
    <Flex
      as={Link}
      href={href}
      display={isHidden ? "none" : "flex"}
      layerStyle="card.fill.light"
      bg={baseColor}
      align="Start"
      justify={{ base: "space-between", lg: "start" }}
      flexDir={{ base: "column", md: "row" }}
      gap={{ base: "0", md: "smallest" }}
      pb="0"
      overflow="hidden"
      width="100%"
      maxW={{ base: "100%", sm: "cardMaxW", md: "700px", lg: "100%" }}
      mx="auto"
      pos="relative"
      transition="all 0.15s ease-in-out"
      _focus={{ outline: "none !important" }}
      _focusVisible={{
        outline: "none !important",
        boxShadow: "outline",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box
        aria-hidden="true"
        pos="absolute"
        inset={0}
        bgImage={bgImage}
        bgSize="cover"
        bgPosition="left"
        bgRepeat="no-repeat"
        zIndex={1}
        opacity={isHovered ? 0.5 : 1}
        transition="opacity 0.2s ease-out"
      />
      <Flex
        align="start"
        justify="start"
        layerStyle="card.textWrapper.fill"
        w={{ base: "100%", lg: variant === PlatformVariants.AGENTS ? "55%" : "75%", xl: "75%" }}
        maxW={{ base: "100%", md: "cardMaxW", lg: "600px" }}
        p={{ base: "big", lg: "bigger", xl: "biggest" }}
        pr={{ base: "big", lg: "small", xl: "big" }}
        m="0"
        zIndex={1}
      >
        <Chip>{eyebrow}</Chip>
        <Text as="h3" textStyle={{ base: "headline3", md: "headline2" }}>
          {title}
        </Text>
        {description && (
          <Text color="brand.textSecondary.light" textStyle="caption1">
            {description}
          </Text>
        )}
        <DirectionalButton as="div" size="sm" variant="tertiary" pl={0} mt="auto" pointerEvents="none">
          {ctaText}
        </DirectionalButton>
      </Flex>

      <Flex display={{ base: "none", md: "flex" }} align="center" justify="center" zIndex={1} height="100%">
        {media}
      </Flex>
    </Flex>
  );
}
